import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Close } from '@mui/icons-material';
import { useGenericModal } from '../../context/GenericModalContext';

interface GenericModalProps {
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  isCentered?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  resizable?: boolean; // 👈 new
}

const sizeClasses = {
  sm: 400,
  md: 600,
  lg: 800,
  xl: 1000,
};

const GenericModal: React.FC<GenericModalProps> = ({
  title,
  size = 'md',
  className = '',
  closeOnEscape = true,
  closeOnOutsideClick = true,
  isCentered = true,
  onOpen,
  onClose,
  resizable = false, // 👈 default off
}) => {
  const { isOpen, closeModal, modalContent } = useGenericModal();
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);

  const [dimensions, setDimensions] = useState<{ width: number }>({
    width: sizeClasses[size],
  });

  // Handle keyboard events
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        closeModal();
      }
    };

    // Focus trap
    const handleTab = (event: KeyboardEvent) => {
      if (!modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === 'Tab') {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTab);
      firstFocusableRef.current = document.activeElement as HTMLElement;
      modalRef.current?.querySelector<HTMLElement>('[tabindex="0"], button, input')?.focus();
      onOpen?.();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
      firstFocusableRef.current?.focus();
      if (!isOpen) onClose?.();
    };
  }, [isOpen, closeModal, closeOnEscape, onOpen, onClose]);

  // Handle outside click
  const handleOutsideClick = (event: React.MouseEvent) => {
    if (closeOnOutsideClick && event.target === event.currentTarget) {
      closeModal();
    }
  };

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = dimensions.width;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(300, startWidth + (moveEvent.clientX - startX));
      setDimensions({ width: newWidth });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleOutsideClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
        >
          <motion.div
            ref={modalRef}
            style={{ width: dimensions.width }}
            className={`
              bg-white rounded-lg shadow-xl 
              w-auto max-w-full p-6 relative
              ${isCentered ? 'mx-auto' : 'mt-10'}
              ${className}
            `}
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
              aria-label="Close modal"
            >
              <Close />
            </button>
            {title && (
              <h2 id="modal-title" className="text-xl font-semibold mb-4">
                {title}
              </h2>
            )}
            <div className="modal-content max-h-[70vh] overflow-y-auto">
              {modalContent}
            </div>

            {/* Resize handle (bottom-right corner) */}
            {resizable && (
              <div
                onMouseDown={handleMouseDown}
                className="absolute bottom-2 right-2 w-4 h-4 cursor-se-resize bg-transparent"
              >
                <div className="w-full h-full border-r-2 border-b-2 border-gray-400" />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenericModal;
